import { BoardState, HandState, Orientation } from '../types/';
import {
    pick,
    sortBy,
    difference,
    concat,
    first,
    compact,
    includes,
    map,
    find,
    reject,
    size,
} from 'lodash';

export type State = {
    hand: HandState,
    board: BoardState,
    commandsRun: string[],
};

export const ADD_TILES_TO_BOARD_FROM_HAND = 'ADD_TILES_TO_BOARD_FROM_HAND';
export const ADD_TILES_TO_HAND_FROM_DECK = 'ADD_TILES_TO_HAND_FROM_DECK';
export const ADD_TILES_TO_HAND_FROM_BOARD = 'ADD_TILES_TO_HAND_FROM_BOARD';
export const ADD_TILES_TO_DECK_FROM_HAND = 'ADD_TILES_TO_DECK_FROM_HAND';
export const MOVE_TILES_ON_BOARD = 'MOVE_TILES_ON_BOARD';
export const INCREMENT_COMMANDS_RUN = 'INCREMENT_COMMANDS_RUN';
export const HYDRATE_STATE = 'HYDRATE_STATE';

interface HydrateStateAction {
    type: typeof HYDRATE_STATE;
    payload: { state: Pick<State, 'hand' | 'board'> };
}

interface AddTilesToHandFromBoardAction {
    type: typeof ADD_TILES_TO_HAND_FROM_BOARD;
    payload: { ids: number[] };
}
interface AddTilesToBoardFromHandAction {
    type: typeof ADD_TILES_TO_BOARD_FROM_HAND;
    payload: { tiles: number[], row: number, column: number, orientation: Orientation };
}

interface AddTilesToHandFromDeckAction {
    type: typeof ADD_TILES_TO_HAND_FROM_DECK;
    payload: { tiles: number[] };
}

interface AddTilesToDeckFromHandAction {
    type: typeof ADD_TILES_TO_DECK_FROM_HAND;
    payload: { tiles: number[] };
}

interface MoveTilesOnBoardAction {
    type: typeof MOVE_TILES_ON_BOARD;
    payload: { ids: number[], deltaColumn: number, deltaRow: number };
}

interface IncrememntCommandsRun {
    type: typeof INCREMENT_COMMANDS_RUN;
    payload: { ids: string[] };
}

export type ReducerActionTypes =
    | IncrememntCommandsRun
    | HydrateStateAction
    | AddTilesToHandFromBoardAction
    | AddTilesToBoardFromHandAction
    | AddTilesToHandFromDeckAction
    | AddTilesToDeckFromHandAction
    | MoveTilesOnBoardAction;

export function incrementCommandsRun(ids: string[]): ReducerActionTypes {
    return {
        type: INCREMENT_COMMANDS_RUN,
        payload: { ids },
    };
}

export function hydrateState(state: Pick<State, 'hand' | 'board'>): ReducerActionTypes {
    return {
        type: HYDRATE_STATE,
        payload: { state },
    };
}

export function addTilesToHandFromBoard(ids: number[]): ReducerActionTypes {
    return {
        type: ADD_TILES_TO_HAND_FROM_BOARD,
        payload: {
            ids,
        },
    };
}

export function addTilesToBoardFromHand(
    tiles: number[],
    row: number,
    column: number,
    orientation: Orientation,
): ReducerActionTypes {
    return {
        type: ADD_TILES_TO_BOARD_FROM_HAND,
        payload: {
            tiles,
            row,
            column,
            orientation,
        },
    };
}

export function addTilesToHandFromDeck(tiles: number[]): ReducerActionTypes {
    return {
        type: ADD_TILES_TO_HAND_FROM_DECK,
        payload: {
            tiles,
        },
    };
}

export function addTilesToDeckFromHand(tiles: number[]): ReducerActionTypes {
    return {
        type: ADD_TILES_TO_DECK_FROM_HAND,
        payload: {
            tiles,
        },
    };
}

export function moveTilesOnBoard({
    ids,
    deltaRow,
    deltaColumn,
}: {
    ids: number[],
    deltaRow: number,
    deltaColumn: number,
}): ReducerActionTypes {
    return {
        type: MOVE_TILES_ON_BOARD,
        payload: { ids, deltaColumn, deltaRow },
    };
}

export const reducer = (state: State, action: ReducerActionTypes) => {
    console.log(action);
    switch (action.type) {
        case HYDRATE_STATE: {
            return {
                ...state,
                ...action.payload.state,
            };
        }
        case INCREMENT_COMMANDS_RUN: {
            const { payload } = action;
            // If there are no new commands to add, then don't modify the state
            const newIds = difference(payload.ids, state.commandsRun);
            return {
                ...state,
                commandsRun: size(newIds) ? [...state.commandsRun, ...newIds] : state.commandsRun,
            };
        }
        case ADD_TILES_TO_BOARD_FROM_HAND: {
            const { payload } = action;

            const newBoardTiles = map(payload.tiles, (id, idx) => {
                if (payload.orientation === Orientation.VERTICAL) {
                    return {
                        id,
                        row: payload.row - idx,
                        column: payload.column,
                    };
                } else {
                    return {
                        id,
                        row: payload.row,
                        column: payload.column + idx,
                    };
                }
            });

            // If there is any overlap, we need to move the board pieces
            // back to the hand
            const tilesForHand = map(
                // find all existing board tiles that match up
                // with the new board tiles
                //
                // And then reject any of the ones with matching ids
                reject(
                    compact(
                        map(newBoardTiles, ({ row, column }) => find(state.board, { row, column })),
                    ),
                    ({ id }) => includes(payload.tiles, id),
                ),
                ({ id }) => ({ id }),
            );

            return {
                ...state,
                board: [
                    ...reject(
                        state.board,
                        ({ id, column, row }) =>
                            !!find(newBoardTiles, { column, row }) || !!find(newBoardTiles, { id }),
                    ),
                    ...newBoardTiles,
                ],
                hand: [
                    ...reject(state.hand, ({ id }) => includes(payload.tiles, id)),
                    ...tilesForHand,
                ],
            };
        }
        // Peel
        case ADD_TILES_TO_HAND_FROM_DECK:
            return {
                ...state,
                hand: [...state.hand, ...map(action.payload.tiles, (id) => ({ id }))],
            };
        // Dump
        case ADD_TILES_TO_DECK_FROM_HAND:
            return {
                ...state,
                hand: reject(state.hand, ({ id }) => includes(action.payload.tiles, id)),
            };
        case MOVE_TILES_ON_BOARD: {
            const { payload } = action;
            const { ids, deltaColumn, deltaRow } = payload;
            const id = first(ids) || 1;
            const tileAtPreviousPosition = find(state.board, { id });
            if (deltaColumn === 0 && deltaRow === 0) {
                return state;
            }
            // If we couldn't find the existing tile, do nothing
            if (tileAtPreviousPosition) {
                const tileAtNewPosition = find(state.board, {
                    row: tileAtPreviousPosition.row + deltaRow,
                    column: tileAtPreviousPosition.column + deltaColumn,
                });
                const tilesToMove = compact(map(ids, (id) => find(state.board, { id })));
                const movedTiles = map(tilesToMove, (tile) => {
                    return {
                        id: tile.id,
                        column: tile.column + deltaColumn,
                        row: tile.row + deltaRow,
                    };
                });
                // Ignore tiles from the moved set
                const tilesAtDestination = reject(
                    compact(
                        map(movedTiles, ({ column, row }) => find(state.board, { column, row })),
                    ),
                    ({ id }) => {
                        return !!find(movedTiles, { id });
                    },
                );
                return {
                    ...state,
                    // Add back to the hand all tiles that were in new destination
                    hand: [
                        ...state.hand,
                        ...(size(ids) > 1
                            ? map(tilesAtDestination, (tile) => pick(tile, 'id'))
                            : []),
                    ],
                    board: sortBy(
                        [
                            // Remove all the `tilesToMove` from the list
                            // Remove all the tiles that exist at our new positions
                            ...reject(state.board, (tile) =>
                                includes(concat(tilesToMove, tilesAtDestination), tile),
                            ),
                            // Insert all the `tilesToMove` to the list
                            ...movedTiles,
                            // Handle collisions for case of moving one tile
                            ...(size(ids) === 1 &&
                            tileAtNewPosition &&
                            tileAtPreviousPosition &&
                            tileAtNewPosition !== tileAtPreviousPosition
                                ? [
                                      {
                                          ...tileAtPreviousPosition,
                                          id: tileAtNewPosition.id,
                                      },
                                  ]
                                : []),
                        ],
                        'id',
                    ),
                };
            } else {
                return state;
            }
        }
        case ADD_TILES_TO_HAND_FROM_BOARD: {
            const { payload } = action;
            return {
                ...state,
                hand: [
                    // Remove duplicates from the hand first
                    ...reject(state.hand, ({ id }) => {
                        return includes(payload.ids, id);
                    }),
                    // Add back all the hand
                    ...map(payload.ids, (id) => ({ id })),
                ],
                board: reject(state.board, ({ id }) => includes(payload.ids, id)),
            };
        }
        default:
            return state;
    }
};
